import React, { useCallback, useRef, useState } from "react";
import useSWRInfinite                           from "swr/infinite";

import { useGetLoggedIn, useGetProfileData } from "_common/hooks/auth";
import { useSetLoginPopup }                  from "_common/hooks/global";
import toast                                 from "_common/toast";
import {
	getCommentsOnFeed,
	getReactionsOnFeed,
	postCommentOnFeed
} from "_services/api/feed";

import Presentation from "./presentation";

const fetchComments = async params => {
	const { id, pageNo, active } = params;

	if ( !active )
		return;
	const res = await getCommentsOnFeed ( { feedId: parseInt ( id ), pageNo, pageSize: 10 } );

	return res;
};

const fetchLikes = async params => {
	const { id, pageNo, active } = params;

	if ( !active )
		return;

	const res = await getReactionsOnFeed ( { feedId: parseInt ( id ), pageNo, pageSize: 10, reaction: "AGREE" } );

	return res;
};

const component = ( {
	reel,
	open,
	likesCount,
	commentCount,
	updateCommentCount,
	onClose
} ) => {
	const [ activeTab, setActiveTab ]            = useState ( "first" );
	const userProfile                            = useGetProfileData ();
	const isLoggedIn                             = useGetLoggedIn ();
	const setLoginPopup                          = useSetLoginPopup ();
	const [ displayEmoji, setDisplayEmoji ]      = useState ( false );
	const [ focusState, setFocusState ]          = useState ( false );
	const textInput                              = useRef ( null );
	const [ ownCommentsList, setOwnCommentList ] = useState ( [] );

	const { data: comments, error: commentError, size: commentSize, setSize: setCommentSize } = useSWRInfinite (
		index => {
			if ( !open ) return null;

			return { key: "image-comments", pageNo: index + 1, id: reel?.id, active: activeTab === "first" ? true : false, open };
		},
		fetchComments
	);

	const { data: likes, error: likeError, size: likeSize, setSize: setLikeSize } = useSWRInfinite (
		index => {
			return { key: "image-likes", pageNo: index + 1, id: reel?.id, active: activeTab === "second" ? true : false };
		},
		fetchLikes
	);

	React.useEffect ( () => {
		setOwnCommentList ( [] );

		return () => {
			setOwnCommentList ( [] );
		};
	}, [] );

	let __comments                 = [].concat.apply ( [], comments );
	const isLoadingInitialComments = !comments && !commentError;
	const isCommentsEmpty          = comments?.[commentSize - 1]?.length === 0;
	const isLoadingMoreComments    = isLoadingInitialComments || ( commentSize > 0 && comments && typeof comments[commentSize - 1] === "undefined" );
	let __likes                    = [].concat.apply ( [], likes );
	const isLoadingInitialLikes    = !likes && !likeError;
	const isLikesEmpty             = likes?.[likeSize - 1]?.length === 0;
	const isLoadingMoreLikes       = isLoadingInitialLikes || ( likeSize > 0 && likes && typeof likes[likeSize - 1] === "undefined" );

	const [ commentInput, setCommentInput ] = useState ( "" );

	const currentTabParams = {
		data       : activeTab === "first" ? [ ...ownCommentsList, ...__comments ] : __likes,
		isFetching : activeTab === "first" ? isLoadingMoreComments : isLoadingMoreLikes,
		isDepleted : activeTab === "first" ? isCommentsEmpty : isLikesEmpty,
		pageNo     : activeTab === "first" ? commentSize : likeSize
	};

	const addCommentToPost = async () => {
		if ( !isLoggedIn ) {
			onClose ();

			return setLoginPopup ( true );
		}

		if ( !commentInput ) return false;

		try {
			const res = await postCommentOnFeed ( {
				commentMessage : commentInput,
				feedId         : parseInt ( reel.id )
			} );

			setOwnCommentList ( prevState => [ {
				id               : res.comment_id,
				commentMessage   : commentInput,
				sportsFanDetails : {
					name  : userProfile.name,
					photo : userProfile.photo,
					id    : userProfile.id
				},
				sportsFanId: userProfile.id
			}, ...prevState ] );

			updateCommentCount ();
		} catch ( err ) {
			toast.error ( err.displayMsg );
		}

		setCommentInput ( "" );
	};

	const handleTabChange = newVal => {
		setActiveTab ( newVal );
	};

	const handleOnCommentChange = e => {
		setCommentInput ( e.target.value );
	};

	/* For Showing Emoji in text */
	const handleEmojiSelect = useCallback (
		e => {
			/*
        Splitting the id  on (‘-’), before adding ‘0x’ to each element
      */
			let sym        = e.unified.split ( "-" );
			let codesArray = [];

			/*
        Here, unified is in Unicode (hex) so
        we need to add ‘0x’ in the front of the code for it to become an emoji
      */
			sym.forEach ( el => codesArray.push ( "0x" + el ) );
			let emoji = String.fromCodePoint ( ...codesArray );

			setCommentInput ( commentInput + " " + emoji );
			textInput?.current?.focus ();
			setFocusState ( true );
		},
		[ commentInput, focusState, textInput ]
	);

	/* For Toggle Emoji Container */
	const handleToggleEmoji = () => {
		if ( !isLoggedIn ) {
			setLoginPopup ( true );

			return;
		}
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setDisplayEmoji ( prev => !prev );
	};

	const toggleFocusState = () => {
		if ( displayEmoji && focusState )
			textInput?.current?.focus ();
		setFocusState ( prev => !prev );
	};

	const fetchMoreComments = () => {
		setCommentSize ( commentSize + 1 );
	};

	const fetchMoreLikes = () => {
		setLikeSize ( likeSize + 1 );
	};

	return (
		<Presentation
			activeTab             = { activeTab }
			addCommentToPost      = { addCommentToPost }
			commentCount          = { commentCount }
			commentInput          = { commentInput }
			currentTabParams      = { currentTabParams }
			displayEmoji          = { displayEmoji }
			fetchComments         = { fetchComments }
			fetchLikes            = { fetchLikes }
			fetchMoreComments     = { fetchMoreComments }
			fetchMoreLikes        = { fetchMoreLikes }
			focusState            = { focusState }
			handleEmojiSelect     = { handleEmojiSelect }
			handleOnCommentChange = { handleOnCommentChange }
			handleTabChange       = { handleTabChange }
			handleToggleEmoji     = { handleToggleEmoji }
			likesCount            = { likesCount }
			onClose               = { onClose }
			reel                  = { reel }
			setDisplayEmoji       = { setDisplayEmoji }
			textInput             = { textInput }
			toggleFocusState      = { toggleFocusState }
		/>

	);
};

export default component;
